import { DraggableRowsTable } from '@components/draggable-rows-table';
import { EntityType } from '@lib/deals';
import { Divider, Grid, Tooltip, Typography } from '@material-ui/core';
import Fab from '@material-ui/core/Fab';
import AddIcon from '@material-ui/icons/Add';
import React, { useCallback, useMemo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Column } from 'react-table';
import styled from 'styled-components';
import { blue, orange } from '@material-ui/core/colors';
import PersonIcon from '@material-ui/icons/Person';
import MovieIcon from '@material-ui/icons/Movie';
import { EntitySearchMultiPartyP2C } from './entity-search-multi-party-p2c';

export const ScrollSectionTitle = ({ children, icon, subTitle, id }) => (
  <Grid container id={id} spacing={icon ? 2 : 0} alignItems="center">
    {icon && <Grid item>{icon}</Grid>}
    <Grid item>
      <Typography variant="h5">{children}</Typography>
      {subTitle && (
        <Typography color="textSecondary" variant="body2">
          {subTitle}
        </Typography>
      )}
    </Grid>
  </Grid>
);

export const P2CContentSelector = ({ deal, createDeal = false }) => {
  const form = useFormContext();

  const [indexesContent, setIndexesContent] = useState(
    Array.from(Array(deal.associated_content?.length || 0), (_, i) => i)
  );
  const [counterContent, setCounterContent] = useState(deal.associated_content?.length || 0);
  
  const addContent = useCallback((index) => {
    setIndexesContent(prevIndexes => {
      return [...prevIndexes, counterContent];
    });
    setCounterContent(prevCounter => prevCounter + 1);
  }, [indexesContent, counterContent]);
  
  const onChangeDraggedRows = allRowsModified => {
    setIndexesContent(allRowsModified.map(eachRow => eachRow.id));
  };

  const removeContent = useCallback(index => {
    setIndexesContent(prevIndexes => {
      return [...prevIndexes.filter(item => item !== index)];
    });
  }, [deal]);

  const getRowId = React.useCallback((row: any, idx) => {
    return '' + row.id;
  }, []);

  const columns: Column<any>[] = useMemo(
    () => [
      {
        Header: 'Cell',
        id: 'cell',
        Cell: ({ row, idx }) => {
          const isRowDeletetable = row.original.deleteable;
          const index = row.original.id;
          const fieldName = `associated_content[${index}]`;
          return (
            <Controller
              name={fieldName}
              key={index}
              control={form.control}
              defaultValue={
                deal.associated_content?.[index]
                  ? deal.associated_content[index]
                  : {
                      name: '',
                      type: EntityType.content,
                      vtr_id: '',
                      meta: null,
                      relation_type: 'content',
                      order_id: index + 1,
                    }
              }
              render={({ onChange }) => (
                <PaddingBottomEntitySearch key={index}>
                  <EntitySearchMultiPartyP2C
                    label=""
                    fixedType={EntityType.content}
                    defaultValue={
                      deal.associated_content?.[index]
                        ? deal.associated_content[index]
                        : {
                            name: '',
                            type: EntityType.content,
                            vtr_id: '',
                            meta: null,
                            relation_type: 'content',
                            order_id: index + 1,
                          }
                    }
                    hideTypeSelect
                    clearable
                    onChange={onChange}
                    titleEditable
                    indexOfEntities={index}
                    removeDealEntities={removeContent}
                    isDealEntityRemovable={isRowDeletetable}
                    dealData={form.getValues()}
                    isDealEntityRequestCreatable={!createDeal}
                  />
                </PaddingBottomEntitySearch>
              )}
            />
          );
        },
      },
    ],
    [deal.associated_content]
  );

  return (
    <React.Fragment key="content">
      <Grid container justify="space-between" style={{ marginRight: '30px' }}>
        <Grid item>
          <ScrollSectionTitle
            id="deal-content"
            subTitle={'Associated content in P2C Relation'}
            icon={<MovieIcon fontSize="large" style={{ color: orange[700] }} />}>
            Associated Content
          </ScrollSectionTitle>
        </Grid>
        <Grid item style={{ paddingRight: '20px', paddingBottom: '10px' }}>
          <Tooltip title="Add Content">
            <StyledFab onClick={addContent} color="primary" aria-label="add" size="small">
              <AddIcon />
            </StyledFab>
          </Tooltip>
        </Grid>
      </Grid>
      <Divider />
      <br />
      <fieldset style={{ padding: '0.75em', backgroundColor: '#f5f5f5', border: 'none' }}>
        <DraggableRowsTableStyled
          getRowId={getRowId}
          onChange={onChangeDraggedRows}
          columns={columns}
          data={indexesContent.map(idx => ({
            id: idx,
            deleteable: true,
          }))}
          stickyHeader
          size="small"
          hideHeaders
          showDragIcon={false}
          multiSectionName={EntityType.content}
        />
      </fieldset>
    </React.Fragment>
  );
};

export function P2CPersonSelector({ deal, createDeal = false }) {
  const form = useFormContext();
  const [indexesDealPersons, setIndexesDealPersons] = useState(
    Array.from(Array(deal?.persons?.length || 0), (_, i) => i)
  );
  
  const [counterDealPersons, setCounterDealPersons] = useState(deal?.persons?.length || 0);
  const addDealPersons = useCallback(() => {
    setIndexesDealPersons(prevIndexes => {
      return [...prevIndexes, counterDealPersons];
    });
    setCounterDealPersons(prevCounter => prevCounter + 1);
  }, [indexesDealPersons, counterDealPersons]);
  
  const removeDealPersons = useCallback(index => {
    setIndexesDealPersons(prevIndexes => {
      return [...prevIndexes.filter(item => item !== index)];
    });
    const watchOnPersons = form.watch('persons');
    watchOnPersons
      ?.filter((x, idx) => idx !== index)
      .forEach((eachRow, idx) => {
        if (eachRow) eachRow['order_id'] = idx + 1;
      });
  }, [deal]);

  const onChangeDraggedRows = allRowsModified => {
    setIndexesDealPersons(allRowsModified.map(eachRow => eachRow.id));
  };

  const getRowId = React.useCallback((row: any, idx) => {
    return '' + row.id;
  }, []);

  const columns: Column<any>[] = useMemo(
    () => [
      {
        Header: 'Cell',
        id: 'cell',
        Cell: ({ row, idx }) => {
          const isRowDeletetable = row.original.deleteable;
          const index = row.original.id;
          const fieldName = `persons[${index}]`;
          return (
            <Controller
              name={fieldName}
              key={index}
              control={form.control}
              defaultValue={
                deal.persons?.[index]
                  ? deal.persons[index]
                  : {
                      name: '',
                      type: EntityType.person,
                      vtr_id: '',
                      meta: null,
                      relation_type: 'person',
                      order_id: index + 1,
                    }
              }
              render={({ onChange }) => (
                <PaddingBottomEntitySearch key={index}>
                  <EntitySearchMultiPartyP2C
                    label="Person"
                    fixedType={EntityType.person}
                    defaultValue={
                      deal.persons?.[index]
                        ? deal.persons[index]
                        : {
                            name: '',
                            type: EntityType.person,
                            vtr_id: '',
                            meta: null,
                            relation_type: 'person',
                            order_id: index + 1,
                          }
                    }
                    hideTypeSelect
                    clearable
                    onChange={onChange}
                    titleEditable
                    indexOfEntities={index}
                    removeDealEntities={removeDealPersons}
                    isDealEntityRemovable={isRowDeletetable}
                    dealData={form.getValues()}
                    isDealEntityRequestCreatable={!createDeal}
                  />
                </PaddingBottomEntitySearch>
              )}
            />
          );
        },
      },
    ],
    [deal.persons]
  );
  
  return (
    <React.Fragment key="persons">
      <Grid container justify="space-between" style={{ marginRight: '30px' }}>
        <Grid item>
          <ScrollSectionTitle
            id="deal-persons"
            subTitle={'Person for the P2C Relation'}
            icon={<PersonIcon fontSize="large" style={{ color: blue[700] }} />}>
            Person
          </ScrollSectionTitle>
        </Grid>
      </Grid>
      <Divider />
      <br />
      <fieldset style={{ padding: '0.75em', backgroundColor: '#f5f5f5', border: 'none' }}>
        <DraggableRowsTableStyled
          getRowId={getRowId}
          onChange={onChangeDraggedRows}
          columns={columns}
          data={indexesDealPersons.map(idx => ({
            id: idx,
            deleteable: indexesDealPersons.length > 1,
          }))}
          stickyHeader
          size="small"
          hideHeaders
          showDragIcon={false}
        />
      </fieldset>
    </React.Fragment>
  );
}

const DraggableRowsTableStyled = styled(DraggableRowsTable)`
  & .MuiTableCell-root.MuiTableCell-body.MuiTableCell-sizeSmall {
    padding-bottom: 10px;
    padding-top: 10px;
  }
`;

const StyledFab = styled(Fab)`
  &.MuiFab-sizeSmall {
    width: 40px;
  }
  & .MuiSvgIcon-root {
    font-size: 1.4rem;
  }
`;

const PaddingBottomEntitySearch = styled.div`
  margin-bottom: 20px;
  :last-child {
    margin-bottom: 0px;
  }
`; 